import clsx from "clsx";
import { FC } from "react";
import { twMerge } from "tailwind-merge";

import { isSubmissionLate } from "components/materials/page/helpers";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { CheckIcon, CircleExclamationIcon, ClockIcon, VerifiedIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectDefaultSubmissionForUser, selectFullSubmissionById } from "store/selectors";
import type { FullGroupSubmissionType, FullIndividualSubmissionType } from "store/selectors/types";

export type AssessmentStatusIconProps = {
  className?: string;
  assessmentsPublished: boolean;
  canAuthorSection: boolean;
  page: PageType;
  accessId?: string | null;
  selectedSubmission?: FullIndividualSubmissionType | FullGroupSubmissionType;
  iconOnly?: boolean;
  showLate?: boolean;
  selected?: boolean;
};

export const AssessmentStatusIcon: FC<AssessmentStatusIconProps> = ({
  className,
  assessmentsPublished,
  canAuthorSection,
  page,
  accessId,
  selected,
  selectedSubmission,
  showLate,
  iconOnly,
}) => {
  const userDefaultSubmission = useAppSelector((s) =>
    selectDefaultSubmissionForUser(s, page.id, accessId),
  );
  const defaultSubmission = useAppSelector((s) =>
    selectFullSubmissionById(s, userDefaultSubmission?.id),
  );

  const submission = selectedSubmission || defaultSubmission;

  if (!submission)
    return iconOnly ? (
      <div className="text-orange">
        <CircleExclamationIcon />
      </div>
    ) : null;

  const baseClassName = twMerge(
    "flex items-center gap-1 rounded-md",
    !iconOnly && "px-1.5 py-0.5",
    className,
  );

  const isLate = isSubmissionLate(submission, page?.due_at);
  const lateSymbol =
    showLate && isLate ? (
      <div className={clsx(baseClassName, !selected && !iconOnly && "bg-orange-tint-90")}>
        <ClockIcon className="text-orange" />
        {!iconOnly && <div className="text-black-tint-40">{t("assessment_status_icon.late")}</div>}
      </div>
    ) : null;

  if ((!canAuthorSection && !assessmentsPublished) || !submission.assessment) {
    return lateSymbol;
  }

  const assessedDivClassName = clsx(baseClassName, !selected && !iconOnly && "bg-green-tint-90");

  return (
    <div className="flex gap-2">
      <div className={assessedDivClassName}>
        {iconOnly ? (
          submission.official_at ? (
            <VerifiedIcon />
          ) : (
            <CheckIcon />
          )
        ) : (
          <div className="text-black-tint-40">{t("assessment_status_icon.assessed")}</div>
        )}
      </div>

      {lateSymbol}
    </div>
  );
};
